import React, { useState } from 'react'
import clsx from 'clsx'
import { includes, isNil, isEmpty } from 'ramda'
import { useField } from 'formik'

import { makeStyles } from '@material-ui/styles'
import { TextField, CircularProgress, Chip } from '@material-ui/core'
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete'
import { FormattedMessage, useIntl } from 'react-intl'
import CheckBox from 'app/componentV4/CheckBox'
import useFeatureFlag from 'app/configs/useFeatureFlag'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'

const SELECT_ALL = 'select-all'
const REASON_SELECT_OPTION = 'select-option'
const REASON_REMOVE_OPTION = 'remove-option'
const REASON_CLEAR = 'clear'

const useStyle = makeStyles((theme) => ({
  root: {
    '& .MuiAutocomplete-inputRoot': {
      overflowX: 'auto',
      height: 'auto',
      maxHeight: '130px',
      position: 'unset',
    },
    '& .MuiInputAdornment-positionEnd': {
      top: '50%',
      right: '2rem',
      position: 'absolute',
    },
    '& .MuiAutocomplete-hasPopupIcon': {
      paddingRight: '0 !important',
    },
    '& .MuiChip-root': {
      margin: '.25rem',
      backgroundColor: '#0073b1',
      color: '#FFFFFF',
      height: 'auto',
    },
    '& .MuiChip-deleteIcon': {
      color: '#FFFFFF',
    },
    '& .MuiChip-label': {
      padding: '7px 12px',
    },
  },
  rootFlag: {
    '& .MuiAutocomplete-inputRoot': {
      overflowX: 'auto',
      height: 'auto',
      maxHeight: '130px',
      minHeight: '48px',
      position: 'unset',
      padding: '5px 14px 5px 14px !important',
    },
    '& .MuiChip-root': {
      backgroundColor: '#183560',
    },
    '& .MuiChip-deleteIcon': {
      color: '#ffffff',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: '250px',
    },
  },
  rootChipNewVersion: {
    '& .MuiAutocomplete-inputRoot': {
      overflowX: 'auto',
      height: 'auto',
      maxHeight: '130px',
      minHeight: '48px',
      position: 'unset',
      padding: '5px 14px 5px 14px !important',
    },
    '& .MuiChip-root': {
      backgroundColor: '#ffffff',
      color: '#5D7290',
      border: '1px solid #5D7290',
    },
    '& .MuiChip-deleteIcon': {
      color: '#5D7290',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: '250px',
    },
  },
}))

const TopicSelector = (props) => {
  const { name, label, options, groupBy, isLoading, onChange } = props
  const { mes_3832_new_design_compose_message } = useFeatureFlag()
  const classes = useStyle()
  const { formatMessage } = useIntl()

  const [field, meta, helpers] = useField(name)
  const { value } = field
  const { setValue } = helpers
  // eslint-disable-next-line no-unused-vars
  const isError = meta.error !== undefined && meta.error !== null

  const [isOpenOptions, setIsOpendOptions] = useState(false)
  const allSelected = isNil(value) ? value : options.length === value.length

  const handleOnChange = (event, selectedOptions, reason) => {
    let result = []
    if (reason === REASON_CLEAR) result = []
    if (includes(reason, [REASON_SELECT_OPTION, REASON_REMOVE_OPTION])) {
      const filterTopics = options.filter((el) => el.id !== SELECT_ALL)
      const getAllTopic = allSelected ? [] : filterTopics
      const checkSelectAll = selectedOptions.find(
        (option) => option.id === SELECT_ALL,
      )
      result = checkSelectAll ? getAllTopic : selectedOptions
    }

    setValue(result)
    if (onChange) {
      onChange(result)
    }
  }

  const handleRenderOption = (option, { selected }) => {
    const selectAllProps =
      option.id === SELECT_ALL ? { checked: allSelected } : {}

    return (
      <div
        className={clsx('flex', option.id === SELECT_ALL ? '-ml-3' : 'ml-5')}
      >
        <CheckBox checked={selected} {...selectAllProps} />
        <div className="flex my-auto">
          <div>{option.name}</div>
          {!isNil(option.subscribersCount) && (
            <div style={{ marginLeft: '20px' }}>
              <FormattedMessage
                id="createNotification.dialog.topic.selector"
                values={{ subscribersCount: option.subscribersCount }}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  const optionSelectAll = (option, params) => {
    if (isEmpty(option)) return options

    const filter = createFilterOptions()
    const filtered = filter(option, params)

    return [
      {
        name: formatMessage({
          id: 'autocomplete.select.all.option',
        }),
        id: SELECT_ALL,
      },
      ...filtered,
    ]
  }

  const customRenderTags = (value, getTagProps) => {
    return value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index })
      return (
        <div key={option.id}>
          <Chip
            key={key}
            {...tagProps}
            label={option.name}
            deleteIcon={<CloseOutlinedIcon fontSize="small" />}
            variant="outlined"
          />
        </div>
      )
    })
  }

  return (
    <>
      <Autocomplete
        {...field}
        name={name}
        id={`${name}-autocomplete`}
        multiple
        open={isOpenOptions}
        onOpen={() => setIsOpendOptions(true)}
        onClose={() => setIsOpendOptions(false)}
        disableCloseOnSelect={true}
        options={options}
        value={value}
        groupBy={() => groupBy}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, value) => option.id === value.id}
        disabled={isEmpty(value) && isLoading}
        renderOption={handleRenderOption}
        {...(mes_3832_new_design_compose_message && {
          renderTags: customRenderTags,
        })}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              placeholder={value.length === 0 ? label : ''}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <React.Fragment>
                    {isLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                  </React.Fragment>
                ),
              }}
            />
          </>
        )}
        filterOptions={optionSelectAll}
        onChange={handleOnChange}
        className={clsx(
          mes_3832_new_design_compose_message
            ? classes.rootChipNewVersion
            : classes.rootFlag,
          'bg-white rounded-8 border border-grey-input',
        )}
      />
    </>
  )
}

export default React.memo(TopicSelector)
